import { useMemo } from "react";
import { useAuth } from "../stores/auth";
import usePermissionsMine from "./queries/usePermissionsMine";

type UserPermission = {
  operation_klass: string;
  description: string;
  display_name: string;
  properties?: { [key: string]: any; };
};

type UseUserPermissionsReturn = {
  isUserPermissionsPending?: boolean;
  getUserPermission: (operationKlass: string, withProperty?: boolean) => UserPermission | undefined;
  hasUserPermission: (operationKlass: string, properties?: string[]) => boolean;
  hasUserPermissionWithActionerKlass: (operationKlass: string, actionerKlassList: string[]) => boolean;
  hasUserPermissionWithProperty: (permission: {
    operationKlass: string;
    propertyKey: string;
    propertyValue: string | number | boolean | null | undefined;
  }) => boolean;
  userPermissionsSet: Set<string>;
};

const useUserPermissions = (): UseUserPermissionsReturn => {
  const { accessToken } = useAuth();

  const {
    data: permissions,
    isPending: permissionsIsPending,
    dataUpdatedAt: permissionsUpdatedAt,
  } = usePermissionsMine({ enabled: Boolean(accessToken) });

  const userPermissionsSet = useMemo(
    () => new Set((permissions as UserPermission[] | undefined)?.map(({ operation_klass }) => operation_klass)),
    [permissionsUpdatedAt]
  );

  const hasUserPermission = (operationKlass: string, properties?: string[]) => {
    if (!properties || properties.length === 0) {
      return userPermissionsSet.has(operationKlass);
    } else {
      const userPermission = getUserPermission(operationKlass, true);

      return properties.every((property) => userPermission?.properties && userPermission?.properties[property]);
    }
  };

  const getUserPermission = (operationKlass: string, withProperty?: boolean) => {
    return permissions?.find(({ operation_klass, properties }) => {
      if (withProperty === true) {
        return operation_klass === operationKlass && properties && Object.keys(properties).length;
      } else if (withProperty === false) {
        return operation_klass === operationKlass && (!properties || (properties && Object.keys(properties).length === 0));
      } else {
        return operation_klass === operationKlass;
      }
    });
  };

  const hasUserPermissionWithActionerKlass = (operationKlass: string, actionerKlassList: string[]) => {
    const userPermissions = permissions as UserPermission[] | undefined;

    if (!userPermissions) return false;

    return userPermissions.some(permission =>
      permission.operation_klass === operationKlass &&
      permission.properties?.actioner_klass &&
      actionerKlassList.includes(permission.properties.actioner_klass)
    );
  };

  const hasUserPermissionWithProperty: UseUserPermissionsReturn['hasUserPermissionWithProperty'] = ({
    operationKlass,
    propertyKey,
    propertyValue,
  }) => {
    const userPermissions = permissions as UserPermission[] | undefined;

    if (!userPermissions) return false;

    return userPermissions.some(permission =>
      permission.operation_klass === operationKlass &&
      permission.properties?.[propertyKey] &&
      permission.properties?.[propertyKey] === propertyValue,
    );
  };

  return {
    isUserPermissionsPending: permissionsIsPending,
    getUserPermission,
    hasUserPermission,
    hasUserPermissionWithActionerKlass,
    hasUserPermissionWithProperty,
    userPermissionsSet,
  };
};

export default useUserPermissions;
